import React, { useEffect, useRef, useState } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { createStyles } from 'utils/createStyle';
import { logP2PInviteFamily } from 'config/analytics';
import { useFirebaseConfigValues } from 'hooks/useFirebaseConfigValues';
import { Value } from 'firebase/remote-config';
import { buildAppsFlyerEnrollmentOneLinkUrl } from 'common/buildAppsFlyerEnrollmentOneLinkUrl';
import {
    getAppP2PInviteUtmParams,
    getWebP2PInviteUtmParams,
    WebP2PInviteShareMethod,
    WebP2PInviteSource,
} from 'common/p2pInviteUtm';
import { toQueryString } from 'common/utils/queryString';

const styles = createStyles({
    container: [
        'flex flex-col items-center justify-center min-h-screen bg-white',
        {
            padding: '2rem',
        },
    ],
    loadingSpinner: ['animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600'],
    errorMessage: [
        'text-red-600 text-center',
        {
            maxWidth: '400px',
            marginTop: '1rem',
        },
    ],
});

export type InviteSource = 'share' | 'email' | 'text';

const APPSFLYER_CONFIG = {
    'web-email': { c: 'P2P', pid: 'web_invite' },
    'web-text': { c: 'P2P', pid: 'web_invite' },
    'web-share': { c: 'P2P', pid: 'web_invite' },
    'app-email': { c: 'P2P', pid: 'app_invite' },
    'app-text': { c: 'P2P', pid: 'app_invite' },
} as const;

export const buildInviteRedirectUrl = ({
    enrollCode,
    platform,
    method,
    enrollPath,
    isWebP2PInvite,
    webP2PInviteSource,
}: {
    enrollCode: string;
    platform: 'web' | 'app';
    method: InviteSource;
    enrollPath: string;
    isWebP2PInvite: boolean;
    webP2PInviteSource?: WebP2PInviteSource;
}) => {
    const configKey = `${platform}-${method}` as keyof typeof APPSFLYER_CONFIG;
    const config = APPSFLYER_CONFIG[configKey] || APPSFLYER_CONFIG['web-email'];
    const p2pParams =
        platform === 'app'
            ? getAppP2PInviteUtmParams(method)
            : isWebP2PInvite && webP2PInviteSource
            ? getWebP2PInviteUtmParams(webP2PInviteSource, method as WebP2PInviteShareMethod)
            : undefined;
    const redirectUrl = buildAppsFlyerEnrollmentOneLinkUrl(enrollCode, config, {
        webEnrollPath: enrollPath,
        // Only app- and explicitly marked web-P2P links carry invite attribution.
        ...(p2pParams
            ? {
                  webEnrollmentQueryParams: p2pParams,
                  appDeepLinkQueryParams: p2pParams,
              }
            : {}),
    });

    return { redirectUrl, p2pParams };
};

const InviteRedirectScreen: React.FC = () => {
    const { platform, method, enrollCode } = useParams<{
        platform: 'web' | 'app';
        method: InviteSource;
        enrollCode: string;
    }>();
    const history = useHistory();
    const location = useLocation();
    const [error, setError] = useState<string | null>(null);
    const isEnrollmentTestVariant = useFirebaseConfigValues(
        'participant_enrollment_test_web',
        false,
        (v: Value) => v.asBoolean()
    );
    const hasLoggedAnalytics = useRef(false);

    useEffect(() => {
        if (!enrollCode) {
            setError('Invalid invite link - no enrollment code provided');
            return;
        }

        if (method && !hasLoggedAnalytics.current) {
            hasLoggedAnalytics.current = true;
            try {
                logP2PInviteFamily(method, 'redirect');
            } catch (analyticsError) {
                console.warn('Analytics logging failed:', analyticsError);
            }
        }

        const enrollPath = isEnrollmentTestVariant ? 'enroll/join-program' : 'enroll';
        const isWebP2PInvite =
            platform === 'web' && new URLSearchParams(location.search).get('p2p') === 'true';
        const webP2PInviteSource = new URLSearchParams(location.search).get('utm_source');
        const { redirectUrl, p2pParams } = buildInviteRedirectUrl({
            enrollCode,
            platform,
            method,
            enrollPath,
            isWebP2PInvite,
            webP2PInviteSource:
                webP2PInviteSource === 'desktop_invite' || webP2PInviteSource === 'mweb_invite'
                    ? webP2PInviteSource
                    : undefined,
        });

        const timeoutId = setTimeout(() => {
            try {
                window.location.href = redirectUrl;
            } catch (err) {
                // if for some reason redirect fails, navigate to enroll page with the enrollment code
                const fallbackQuery = p2pParams ? `?${toQueryString(p2pParams)}` : '';
                history.push(`/enroll/${enrollCode}${fallbackQuery}`);
            }
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [enrollCode, method, platform, isEnrollmentTestVariant, history, location.search]);

    if (error) {
        return (
            <div className={styles.container}>
                <div className={styles.errorMessage}>{error}</div>
                <button
                    onClick={() => history.push('/')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                    Go to Homepage
                </button>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.loadingSpinner}></div>
        </div>
    );
};

export default InviteRedirectScreen;
